import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import DataTable from "react-data-table-component";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Badge as BadgeComponent,
  Input,
  Label,
  FormGroup,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { getBadgeList, getBadgeDetails, deleteBadge, createBadge, editBadge } from "./badgeApi";

const BadgePage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [addModal, setAddModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [selectedBadge, setSelectedBadge] = useState(null);
  const [badgeDetails, setBadgeDetails] = useState(null);
  
  // Form state for adding badge
  const [formData, setFormData] = useState({
    badge_key: "",
    badge_name: "",
    description: "",
    badge_type: "system",
  });
  const [formLoading, setFormLoading] = useState(false);
  
  // Form state for editing badge
  const [editFormData, setEditFormData] = useState({
    badge_id: null,
    badge_key: "",
    badge_name: "",
    description: "",
    badge_type: "system",
  });
  const [editFormLoading, setEditFormLoading] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(50);
  const [totalRows, setTotalRows] = useState(0);

  // Refs for optimization
  const abortControllerRef = useRef(null);
  const isMountedRef = useRef(true);
  const isLoadingRef = useRef(false);
  const hasInitialFetchedRef = useRef(false);

  const columns = useMemo(
    () => [
      {
        name: <span className="font-weight-bold fs-13">Badge ID</span>,
        selector: (row) => row.badge_id,
        sortable: true,
      },
      {
        name: <span className="font-weight-bold fs-13">Badge Key</span>,
        selector: (row) => row.badge_key,
        sortable: true,
      },
      {
        name: <span className="font-weight-bold fs-13">Badge Name</span>,
        selector: (row) => row.badge_name,
        sortable: true,
      },
      {
        name: <span className="font-weight-bold fs-13">Badge Type</span>,
        selector: (row) => row.badge_type,
        sortable: true,
      },
      {
        name: <span className="font-weight-bold fs-13">User Count</span>,
        selector: (row) => row.user_count,
        sortable: true,
        cell: (row) => (
          <BadgeComponent color="info" className="px-2 py-1">
            {row.user_count || 0}
          </BadgeComponent>
        ),
      },
      {
        name: <span className="font-weight-bold fs-13">Created At</span>,
        selector: (row) => row.created_at,
        sortable: true,
        cell: (row) => {
          if (!row.created_at) return "-";
          const date = new Date(row.created_at);
          return date.toLocaleString();
        },
      },
      {
        name: <span className="font-weight-bold fs-13">Updated At</span>,
        selector: (row) => row.updated_at,
        sortable: true,
        cell: (row) => {
          if (!row.updated_at) return "-";
          const date = new Date(row.updated_at);
          return date.toLocaleString();
        },
      },
      {
        name: <span className="font-weight-bold fs-13">Action</span>,
        cell: (row) => (
          <div className="d-flex gap-2">
            <Button
              color="info"
              size="sm"
              onClick={() => handleView(row)}
              className="px-3"
            >
              View
            </Button>
            <Button
              color="danger"
              size="sm"
              onClick={() => handleDeleteClick(row)}
              className="px-3"
            >
              Delete
            </Button>
          </div>
        ),
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
      },
    ],
    []
  );

  const fetchList = useCallback(async () => {
    if (isLoadingRef.current) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    isLoadingRef.current = true;
    setLoading(true);

    try {
      const filters = {
        limit,
        page: currentPage,
      };

      const result = await getBadgeList(filters);

      if (abortController.signal.aborted || !isMountedRef.current) {
        return;
      }

      setData(result.data);
      setTotalRows(result.pagination.totalRecords);
    } catch (error) {
      if (error.name !== "AbortError" && isMountedRef.current) {
        console.error("Error fetching badge list:", error);
      }
    } finally {
      if (isMountedRef.current && !abortController.signal.aborted) {
        setLoading(false);
        isLoadingRef.current = false;
      }
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }
    }
  }, [currentPage, limit]);

  const handleView = useCallback(async (badge) => {
    setSelectedBadge(badge);
    setViewModal(true);
    
    try {
      const details = await getBadgeDetails(badge.badge_id);
      setBadgeDetails(details);
    } catch (error) {
      console.error("Error fetching badge details:", error);
      // If API fails, use the row data
      setBadgeDetails(badge);
    }
  }, []);

  const handleDeleteClick = useCallback((badge) => {
    setSelectedBadge(badge);
    setDeleteModal(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!selectedBadge) return;

    try {
      // Delete the badge
      await deleteBadge(selectedBadge.badge_id);
      
      // Remove the badge from the list without refetching
      setData((prevData) => prevData.filter((badge) => badge.badge_id !== selectedBadge.badge_id));
      setTotalRows((prev) => Math.max(0, prev - 1));
      
      setDeleteModal(false);
      setSelectedBadge(null);
    } catch (error) {
      console.error("Error deleting badge:", error);
      alert("Failed to delete badge. Please try again.");
    }
  }, [selectedBadge]);

  // Initial fetch
  useEffect(() => {
    isMountedRef.current = true;
    if (!hasInitialFetchedRef.current) {
      fetchList();
      hasInitialFetchedRef.current = true;
    }

    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Fetch when page changes
  useEffect(() => {
    if (hasInitialFetchedRef.current) {
      fetchList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const toggleViewModal = () => {
    setViewModal(!viewModal);
    if (!viewModal) {
      setSelectedBadge(null);
      setBadgeDetails(null);
    }
  };

  const toggleDeleteModal = () => {
    setDeleteModal(!deleteModal);
    if (!deleteModal) {
      setSelectedBadge(null);
    }
  };

  const toggleAddModal = () => {
    setAddModal(!addModal);
    if (!addModal) {
      // Reset form when closing
      setFormData({
        badge_key: "",
        badge_name: "",
        description: "",
        badge_type: "system",
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEditClick = () => {
    if (!badgeDetails) return;
    
    // Populate edit form with current badge details
    setEditFormData({
      badge_id: badgeDetails.badge_id,
      badge_key: badgeDetails.badge_key || "",
      badge_name: badgeDetails.badge_name || "",
      description: badgeDetails.description || "",
      badge_type: badgeDetails.badge_type || "system",
    });
    
    // Close view modal and open edit modal
    setViewModal(false);
    setEditModal(true);
  };

  const toggleEditModal = () => {
    setEditModal(!editModal);
    if (!editModal) {
      // Reset form when closing
      setEditFormData({
        badge_id: null,
        badge_key: "",
        badge_name: "",
        description: "",
        badge_type: "system",
      });
    }
  };

  const handleAddBadge = useCallback(async () => {
    if (!formData.badge_key || !formData.badge_name || !formData.description || !formData.badge_type) {
      alert("Please fill in all fields");
      return;
    }

    setFormLoading(true);
    try {
      const newBadge = await createBadge(formData);
      
      // Ensure user_count is set (new badges start with 0 users)
      const badgeWithUserCount = {
        ...newBadge,
        user_count: newBadge.user_count || 0,
      };
      
      // Append the new badge to the list
      setData((prevData) => [badgeWithUserCount, ...prevData]);
      setTotalRows((prev) => prev + 1);
      
      // Reset form and close modal
      setFormData({
        badge_key: "",
        badge_name: "",
        description: "",
        badge_type: "system",
      });
      setAddModal(false);
    } catch (error) {
      console.error("Error creating badge:", error);
      alert("Failed to create badge. Please try again.");
    } finally {
      setFormLoading(false);
    }
  }, [formData]);

  const handleEditBadge = useCallback(async () => {
    if (!editFormData.badge_id || !editFormData.badge_key || !editFormData.badge_name || !editFormData.description || !editFormData.badge_type) {
      alert("Please fill in all fields");
      return;
    }

    setEditFormLoading(true);
    try {
      const updatedBadge = await editBadge(editFormData);
      
      // Preserve user_count from original badge if not in response
      const originalBadge = data.find((badge) => badge.badge_id === updatedBadge.badge_id);
      const badgeWithUserCount = {
        ...updatedBadge,
        user_count: updatedBadge.user_count !== undefined ? updatedBadge.user_count : (originalBadge?.user_count || 0),
      };
      
      // Update the badge in the list
      setData((prevData) =>
        prevData.map((badge) =>
          badge.badge_id === badgeWithUserCount.badge_id ? badgeWithUserCount : badge
        )
      );
      
      // Reset form and close modal
      setEditFormData({
        badge_id: null,
        badge_key: "",
        badge_name: "",
        description: "",
        badge_type: "system",
      });
      setEditModal(false);
    } catch (error) {
      console.error("Error updating badge:", error);
      alert("Failed to update badge. Please try again.");
    } finally {
      setEditFormLoading(false);
    }
  }, [editFormData, data]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Dashboard" breadcrumbItem="Badge" />
          <Row>
            <Col xs={12}>
              <Card>
                <CardHeader className="d-flex justify-content-between align-items-center">
                  <h4 className="card-title mb-0">
                    <i className="mdi mdi-medal me-2"></i>Badge
                  </h4>
                  <Button color="success" onClick={() => setAddModal(true)}>
                    <i className="mdi mdi-plus me-1"></i>Add Badge
                  </Button>
                </CardHeader>
                <CardBody>
                  <DataTable
                    columns={columns}
                    data={data}
                    progressPending={loading}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    paginationDefaultPage={currentPage}
                    onChangePage={(page) => setCurrentPage(page)}
                    paginationPerPage={limit}
                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                    noDataComponent={
                      <div className="text-center py-4">
                        <i className="mdi mdi-medal-outline" style={{ fontSize: "3rem", color: "#6c757d" }}></i>
                        <p className="mt-3 text-muted">No badges found</p>
                      </div>
                    }
                    highlightOnHover
                    pointerOnHover
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* View Modal */}
      <Modal isOpen={viewModal} toggle={toggleViewModal} centered size="lg">
        <ModalHeader toggle={toggleViewModal}>
          Badge Details
        </ModalHeader>
        <ModalBody>
          {badgeDetails && (
            <div>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Badge ID:</strong>
                  <p>{badgeDetails.badge_id || "-"}</p>
                </Col>
                <Col md={6}>
                  <strong>Badge Key:</strong>
                  <p>{badgeDetails.badge_key || "-"}</p>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Badge Name:</strong>
                  <p>{badgeDetails.badge_name || "-"}</p>
                </Col>
                <Col md={6}>
                  <strong>Badge Type:</strong>
                  <p>{badgeDetails.badge_type || "-"}</p>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>User Count:</strong>
                  <p>
                    <BadgeComponent color="info" className="px-2 py-1">
                      {badgeDetails.user_count || 0}
                    </BadgeComponent>
                  </p>
                </Col>
                <Col md={6}>
                  <strong>Created At:</strong>
                  <p>
                    {badgeDetails.created_at
                      ? new Date(badgeDetails.created_at).toLocaleString()
                      : "-"}
                  </p>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Updated At:</strong>
                  <p>
                    {badgeDetails.updated_at
                      ? new Date(badgeDetails.updated_at).toLocaleString()
                      : "-"}
                  </p>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={12}>
                  <strong>Description:</strong>
                  <p>{badgeDetails.description || "-"}</p>
                </Col>
              </Row>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleEditClick}>
            <i className="mdi mdi-pencil me-1"></i>Edit
          </Button>
          <Button color="secondary" onClick={toggleViewModal}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModal} toggle={toggleDeleteModal} centered>
        <ModalHeader toggle={toggleDeleteModal}>
          Delete Badge
        </ModalHeader>
        <ModalBody>
          <div className="text-center">
            <lord-icon
              src="https://cdn.lordicon.com/gsqxdxog.json"
              trigger="loop"
              colors="primary:#f7b84b,secondary:#f06548"
              style={{ width: "100px", height: "100px" }}
            ></lord-icon>
            <div className="mt-4 pt-2 fs-15 mx-4 mx-sm-5">
              <h4>Are you Sure?</h4>
              <p className="text-muted mx-4 mb-0">
                Are you sure you want to delete this badge? This action cannot be undone.
              </p>
            </div>
            <div className="d-flex gap-2 justify-content-center mt-4 mb-2">
              <Button color="light" onClick={toggleDeleteModal}>
                Cancel
              </Button>
              <Button color="danger" onClick={handleDeleteConfirm}>
                Yes, Delete It!
              </Button>
            </div>
          </div>
        </ModalBody>
      </Modal>

      {/* Add Badge Modal */}
      <Modal isOpen={addModal} toggle={toggleAddModal} centered size="lg">
        <ModalHeader toggle={toggleAddModal}>
          <i className="mdi mdi-medal me-2"></i>Add New Badge
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md={12}>
              <FormGroup className="mb-3">
                <Label for="badge_key" className="form-label">
                  Badge Key <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  id="badge_key"
                  name="badge_key"
                  value={formData.badge_key}
                  onChange={handleInputChange}
                  placeholder="e.g., community_builder"
                  required
                />
                <small className="form-text text-muted">
                  Unique identifier for the badge (use lowercase with underscores)
                </small>
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <FormGroup className="mb-3">
                <Label for="badge_name" className="form-label">
                  Badge Name <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  id="badge_name"
                  name="badge_name"
                  value={formData.badge_name}
                  onChange={handleInputChange}
                  placeholder="e.g., Community Builder"
                  required
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <FormGroup className="mb-3">
                <Label for="description" className="form-label">
                  Description <span className="text-danger">*</span>
                </Label>
                <Input
                  type="textarea"
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter badge description"
                  rows="3"
                  required
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <FormGroup className="mb-3">
                <Label for="badge_type" className="form-label">
                  Badge Type <span className="text-danger">*</span>
                </Label>
                <Input
                  type="select"
                  id="badge_type"
                  name="badge_type"
                  value={formData.badge_type}
                  onChange={handleInputChange}
                  required
                >
                  <option value="system">System</option>
                  <option value="engagement">Engagement</option>
                  <option value="reputation">Reputation</option>
                  <option value="tenure">Tenure</option>
                  <option value="event">Event</option>
                </Input>
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={toggleAddModal} disabled={formLoading}>
            Cancel
          </Button>
          <Button color="success" onClick={handleAddBadge} disabled={formLoading}>
            {formLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Creating...
              </>
            ) : (
              <>
                <i className="mdi mdi-check me-1"></i>Create Badge
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Edit Badge Modal */}
      <Modal isOpen={editModal} toggle={toggleEditModal} centered size="lg">
        <ModalHeader toggle={toggleEditModal}>
          <i className="mdi mdi-pencil me-2"></i>Edit Badge
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md={12}>
              <FormGroup className="mb-3">
                <Label for="edit_badge_key" className="form-label">
                  Badge Key <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  id="edit_badge_key"
                  name="badge_key"
                  value={editFormData.badge_key}
                  onChange={handleEditInputChange}
                  placeholder="e.g., community_builder"
                  required
                />
                <small className="form-text text-muted">
                  Unique identifier for the badge (use lowercase with underscores)
                </small>
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <FormGroup className="mb-3">
                <Label for="edit_badge_name" className="form-label">
                  Badge Name <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  id="edit_badge_name"
                  name="badge_name"
                  value={editFormData.badge_name}
                  onChange={handleEditInputChange}
                  placeholder="e.g., Community Builder"
                  required
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <FormGroup className="mb-3">
                <Label for="edit_description" className="form-label">
                  Description <span className="text-danger">*</span>
                </Label>
                <Input
                  type="textarea"
                  id="edit_description"
                  name="description"
                  value={editFormData.description}
                  onChange={handleEditInputChange}
                  placeholder="Enter badge description"
                  rows="3"
                  required
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <FormGroup className="mb-3">
                <Label for="edit_badge_type" className="form-label">
                  Badge Type <span className="text-danger">*</span>
                </Label>
                <Input
                  type="select"
                  id="edit_badge_type"
                  name="badge_type"
                  value={editFormData.badge_type}
                  onChange={handleEditInputChange}
                  required
                >
                  <option value="system">System</option>
                  <option value="engagement">Engagement</option>
                  <option value="reputation">Reputation</option>
                  <option value="tenure">Tenure</option>
                  <option value="event">Event</option>
                </Input>
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={toggleEditModal} disabled={editFormLoading}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleEditBadge} disabled={editFormLoading}>
            {editFormLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Updating...
              </>
            ) : (
              <>
                <i className="mdi mdi-check me-1"></i>Update Badge
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default BadgePage;

